import {Button, Typography} from '@mui/material';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import FirestoreManager from '../../api/firebaseManager';
import {useAuth} from '../../hooks/use-auth';
import {useRamp} from '../../hooks/use-ramp';

const RAMP_REDIRECT_URI =
  process.env.REACT_APP_RAMP_REDIRECT_URI ||
  `${window.location.origin}/rampConnect`;

// Handles Ramp's OAuth redirect (?code&state): verifies state, exchanges the
// code for a per-customer connection, then runs the initial entity/accounts/
// payees sync. Mirrors the Addepar callback pattern.
const RampConnect = () => {
  const {
    state: {profile},
  } = useAuth();
  const {fetchInitialRamp} = useRamp();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState('');
  const [summary, setSummary] = useState(null);

  const hasRunRef = useRef(false);

  const runConnect = useCallback(
    async code => {
      try {
        setProgress('Securing your connection with Ramp...');
        // FOR-2187: the modal stores the checked entities as a JSON list.
        // Fall back to the older single-entity key for in-flight connects.
        let entityNames = [];
        try {
          entityNames = JSON.parse(
            localStorage.getItem('ramp_entity_names') || '[]',
          );
        } catch (parseErr) {
          entityNames = [];
        }
        if (!entityNames.length) {
          const legacy = localStorage.getItem('ramp_entity_name');
          if (legacy) entityNames = [legacy];
        }

        const exchange = await FirestoreManager.exchangeRampToken({
          code,
          redirectUri: RAMP_REDIRECT_URI,
          customerID: profile.customerID,
          entityName: entityNames[0] || null,
          entityNames,
          uid: profile.uid,
        });

        if (!exchange?.success) {
          throw new Error(exchange?.error || 'Failed to complete Ramp authorization');
        }
        const connectionId = exchange.data?.connection_id;

        // Initial sync. Failures per-step don't undo the connection; each can
        // be re-run from the Ramp connection page.
        setProgress('Syncing entity to Ramp...');
        const entities = await FirestoreManager.uploadRampEntities(connectionId);
        setProgress('Syncing chart of accounts to Ramp...');
        const accounts = await FirestoreManager.uploadRampAccounts(connectionId);
        setProgress('Syncing payees to Ramp...');
        const vendors = await FirestoreManager.uploadRampVendors(connectionId);

        // Show created/updated/unchanged - a bare count reads as "0 synced"
        // on a re-connect where everything already exists in Ramp.
        const line = (label, r, key) => {
          if (!r?.success) return `${label}: failed (${r?.error || 'unknown'})`;
          const c = r?.counts;
          if (c) {
            return (
              `${label}: ${c.created} created, ${c.updated} updated, ` +
              `${c.unchanged} unchanged`
            );
          }
          return `${label}: ${r?.data?.[key] ?? r?.count ?? 'synced'}`;
        };
        setSummary([
          'Connection: created',
          line('Entity', entities, 'uploaded'),
          line('Accounts', accounts, 'uploaded'),
          line('Payees', vendors, 'uploaded'),
        ]);

        localStorage.removeItem('ramp_oauth_state');
        localStorage.removeItem('ramp_entity_name');
        localStorage.removeItem('ramp_entity_names');

        // Refresh dashboard state so the new connection appears.
        if (profile?.uid) fetchInitialRamp(profile.uid);
        setStatus('success');
      } catch (err) {
        localStorage.removeItem('ramp_oauth_state');
        localStorage.removeItem('ramp_entity_name');
        localStorage.removeItem('ramp_entity_names');
        setError(err.message || 'Failed to complete Ramp connection');
        setStatus('error');
      }
    },
    [profile, fetchInitialRamp],
  );

  useEffect(() => {
    if (hasRunRef.current) return;
    if (!profile?.uid) return; // wait for profile to load

    const code = searchParams.get('code');
    const returnedState = searchParams.get('state');
    const oauthError = searchParams.get('error');
    const expectedState = localStorage.getItem('ramp_oauth_state');

    if (oauthError) {
      setError(`Ramp denied the authorization (${oauthError}).`);
      setStatus('error');
      hasRunRef.current = true;
      return;
    }
    if (!code) {
      setError('No authorization code received from Ramp.');
      setStatus('error');
      hasRunRef.current = true;
      return;
    }
    // CSRF protection: the state we sent must match the one Ramp returned.
    if (expectedState && returnedState && expectedState !== returnedState) {
      setError('Authorization state mismatch. Please try connecting again.');
      setStatus('error');
      hasRunRef.current = true;
      return;
    }

    hasRunRef.current = true;
    runConnect(code);
  }, [profile?.uid, searchParams, runConnect]);

  return (
    <div className="page-container">
      <div style={{maxWidth: '640px', margin: '40px auto'}}>
        <Typography variant="h6" sx={{mb: 2}}>
          Ramp Connection
        </Typography>

        {status === 'loading' && (
          <Typography sx={{fontStyle: 'italic'}}>
            {progress || 'Connecting to Ramp...'}
          </Typography>
        )}

        {status === 'success' && (
          <>
            <Typography sx={{mb: 2}}>
              Your Ramp connection is complete.
            </Typography>
            {summary && (
              <ul>
                {summary.map((l, i) => (
                  <li key={i}>{l}</li>
                ))}
              </ul>
            )}
            <Button
              onClick={() => navigate('/dashboard')}
              style={{
                backgroundColor: '#c5c7c6',
                color: 'black',
                marginTop: '16px',
              }}>
              Return to Dashboard
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Typography sx={{color: 'red', mb: 2}}>{error}</Typography>
            <Button
              onClick={() => navigate('/dashboard')}
              style={{backgroundColor: '#c5c7c6', color: 'black'}}>
              Return to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default RampConnect;
